import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Send, Copy, Plus, Clock, UserCheck, ShoppingBag, CheckCircle2, MessageCircle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import api, { formatApiError } from "../../lib/api";

const STATUS_META = {
  invited:   { label: "Invited",   icon: Clock,        cls: "bg-slate-100 text-slate-600" },
  signed_up: { label: "Signed up", icon: UserCheck,    cls: "bg-blue-50 text-blue-600" },
  purchased: { label: "Purchased — pending confirmation", icon: ShoppingBag, cls: "bg-amber-50 text-amber-700" },
  confirmed: { label: "Confirmed · +500 pts", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700" },
};

function derivedStatus(r) {
  if (r.status === "confirmed") return "confirmed";
  if (r.status === "signed_up" && r.purchased) return "purchased";
  return r.status || "invited";
}

const NUDGE_AFTER_DAYS = 3;

function daysSince(iso) {
  if (!iso) return 0;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.floor((Date.now() - then) / 86400000);
}

// Rebuild the share message client-side (mirrors backend _referral_share).
function shareFor(r) {
  const link = `${window.location.origin}/register?ref=${r.referrer_code || ""}${r.invite_token ? `&inv=${r.invite_token}` : ""}`;
  const fn = (r.friend_name || "there").split(" ")[0];
  const sms = `Hi ${fn}! I love Iridescent Medical & Wellness Spa. Join their free rewards with my link and we both get perks - see you there! ${link}`;
  return { link, sms };
}

export function MyReferralsCard() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [friends, setFriends] = useState([{ name: "", phone: "" }, { name: "", phone: "" }]);
  const [invites, setInvites] = useState(null);
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/member/referrals")
      .then((r) => setList(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setFriend = (i, k, v) => setFriends((fs) => fs.map((f, idx) => (idx === i ? { ...f, [k]: v } : f)));

  const send = async () => {
    const payload = friends.filter((f) => f.name.trim());
    if (!payload.length) { toast.error("Add at least one friend's name"); return; }
    setSending(true);
    try {
      const res = await api.post("/member/referrals/invite", { friends: payload });
      setInvites(res.data.invites);
      toast.success("Invites ready — send them off!");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create invites");
    } finally {
      setSending(false);
    }
  };

  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Message copied — paste it to your friend!"); };

  const resetForm = () => {
    setShowForm(false);
    setInvites(null);
    setFriends([{ name: "", phone: "" }, { name: "", phone: "" }]);
  };

  return (
    <div className="bg-white rounded-2xl p-7 border border-slate-100 mb-6" data-testid="my-referrals-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-iri-mid" strokeWidth={1.5} />
          <h2 className="font-heading text-xl font-medium text-ink">My referrals</h2>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} data-testid="referral-invite-open"
            className="iri-gradient text-ink rounded-full h-10 gap-2">
            <Plus className="h-4 w-4" /> Invite friends
          </Button>
        )}
      </div>

      {/* invite form */}
      {showForm && (
        <div className="mt-5 rounded-xl bg-stone p-4" data-testid="referral-invite-form">
          {!invites ? (
            <div className="space-y-4">
              <p className="text-sm text-[#5C6664]">
                Invite up to 2 friends. When a friend joins and makes their first purchase, you earn
                <strong className="text-ink"> 500 points</strong> after our team confirms it.
              </p>
              {friends.map((f, i) => (
                <div key={i} className="grid sm:grid-cols-2 gap-2" data-testid={`referral-friend-${i}`}>
                  <Input value={f.name} onChange={(e) => setFriend(i, "name", e.target.value)} placeholder={`Friend ${i + 1} name`} className="rounded-lg h-10 bg-white" data-testid={`referral-name-${i}`} />
                  <Input value={f.phone} onChange={(e) => setFriend(i, "phone", e.target.value)} placeholder="Mobile (optional)" className="rounded-lg h-10 bg-white" data-testid={`referral-phone-${i}`} />
                </div>
              ))}
              <div className="flex gap-2">
                <Button onClick={send} disabled={sending} data-testid="referral-send"
                  className="iri-gradient text-ink rounded-full h-11 flex-1 font-medium">
                  <Send className="h-4 w-4 mr-2" /> {sending ? "Preparing…" : "Create invites"}
                </Button>
                <button onClick={resetForm} data-testid="referral-cancel" className="px-4 text-sm text-[#8A9492] hover:text-ink">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="referral-invite-results">
              {invites.map((inv, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`referral-invite-${i}`}>
                  <p className="text-sm text-ink font-medium">{inv.friend_name}</p>
                  <div className="flex gap-2 mt-2">
                    {inv.friend_phone && (
                      <a href={`sms:${inv.friend_phone}?&body=${encodeURIComponent(inv.sms)}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-full iri-gradient text-ink text-sm font-medium"
                        data-testid={`referral-text-${i}`}>
                        <Send className="h-3.5 w-3.5" /> Text {inv.friend_name.split(" ")[0]}
                      </a>
                    )}
                    <button onClick={() => copy(inv.sms)} data-testid={`referral-copy-msg-${i}`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-full bg-stone text-ink text-sm">
                      <Copy className="h-3.5 w-3.5" /> Copy message
                    </button>
                  </div>
                </div>
              ))}
              <Button onClick={resetForm} data-testid="referral-invite-done"
                className="bg-ink text-white rounded-full h-11 w-full font-medium">
                Done
              </Button>
            </div>
          )}
        </div>
      )}

      {/* status list */}
      <div className="mt-5">
        {loading ? (
          <p className="text-sm text-[#8A9492]">Loading your referrals…</p>
        ) : list.length === 0 ? (
          <p className="text-sm text-[#8A9492]" data-testid="referral-empty">
            No referrals yet. Invite a friend to start earning 500 points per confirmed referral.
          </p>
        ) : (
          <>
            {(() => {
              const stale = list.filter((r) => derivedStatus(r) === "invited" && daysSince(r.created_at) >= NUDGE_AFTER_DAYS);
              if (!stale.length) return null;
              return (
                <div className="mb-4 flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3" data-testid="referral-nudge">
                  <MessageCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" strokeWidth={1.75} />
                  <p className="text-sm text-amber-800">
                    {stale.length} friend{stale.length > 1 ? "s haven't" : " hasn't"} joined yet — a quick text goes a long way. Tap
                    <strong> Text again</strong> below to give them a friendly nudge.
                  </p>
                </div>
              );
            })()}
            <ul className="space-y-2.5" data-testid="referral-list">
              {list.map((r, i) => {
                const s = derivedStatus(r);
                const meta = STATUS_META[s] || STATUS_META.invited;
                const Icon = meta.icon;
                const stale = s === "invited" && daysSince(r.created_at) >= NUDGE_AFTER_DAYS;
                const share = s === "invited" ? shareFor(r) : null;
                return (
                  <li key={r.id || i} className={`rounded-xl border px-4 py-3 ${stale ? "border-amber-200 bg-amber-50/40" : "border-slate-100"}`}
                    data-testid={`referral-row-${i}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-ink truncate">{r.friend_name || "Friend"}</p>
                        {r.friend_phone && <p className="text-xs text-[#8A9492] truncate">{r.friend_phone}</p>}
                      </div>
                      <span className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full ${meta.cls}`}
                        data-testid={`referral-status-${i}`}>
                        <Icon className="h-3.5 w-3.5" /> {meta.label}
                      </span>
                    </div>
                    {share && (
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        {stale && <span className="text-xs text-amber-700 mr-auto">Hasn't joined yet — a nudge helps 💬</span>}
                        <div className={`flex gap-2 ${stale ? "" : "ml-auto"}`}>
                          {r.friend_phone && (
                            <a href={`sms:${r.friend_phone}?&body=${encodeURIComponent(share.sms)}`}
                              className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-full iri-gradient text-ink text-sm font-medium"
                              data-testid={`referral-remind-text-${i}`}>
                              <Send className="h-3.5 w-3.5" /> Text again
                            </a>
                          )}
                          <button onClick={() => copy(share.sms)} data-testid={`referral-remind-copy-${i}`}
                            className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-full bg-stone text-ink text-sm">
                            <Copy className="h-3.5 w-3.5" /> Copy message
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
